import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, useTheme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { MarketLink } from '@market/react';
import TagIcon from '@square-icons/react/16/UI/tag';
import Sentry from 'application/exception_reporter';
import {
  STICKY_HEADER_OFFSET,
  TRACKING_EVENT_TYPES,
  eligibleProductPromotions,
  getAddOnByVariantId,
  getAlternativeUrl,
  trackAddToCart,
  trackViewCart,
  getTopPromoEligibility,
} from 'components/pages/product/helpers';
import AccessoryModal from 'components/pages/product/accessory_modal';
import UpsellAccordion from 'components/pages/product/upsell_accordion';
import RichTextContent from 'components/common/contentful/rich_text_content';
import InfoBubble from 'components/common/info_bubble';
import SubtotalBox from './subtotal_box';
import ProductHeading from './product_heading';
import ProductActions from './product_actions';
import FulfillmentSection from 'typescript/pages/product/fulfillment_section';
import ThreePPContent from './three_p_p_content';
import ProductAlternatives from './product_alternatives';
import ProductClient from 'clients/product';
import { updateItem } from 'utilities/cart';
import {
  getCountryCode,
  getLanguageCode,
  localizedShopPath,
} from 'utilities/locales';
import {
  updateCartCount,
  updateCtaSubnav,
  updateCurrentUrl,
} from 'utilities/nav';
import { trackEvent, trackCdp } from 'utilities/tracking';
import ProductAddOn, { NO_ADD_ON_VALUE } from './product_add_on';
import ProductPromotionBanner from 'components/common/product_promotion_banner';
import CartClient from 'clients/cart';
import EligibilityClient from 'clients/eligibility';
import { useFeatureFlags } from 'components/context/feature_flags';
import { R4X_BASE_PRODUCT_KEYS } from 'constants/enums';
import CDP_EVENT_NAMES from 'constants/cdp_events';
import { getSoftwarePageViewed } from 'utilities/cookies';
import ColorProductGroup from 'typescript/components/product_groups/color_product_group';
import {
  allProductsAreColorGrouped,
  createColorProductFromProduct,
  filterInvalidColorProductConfigs,
} from 'typescript/utilities/color_group_utils';
import ProductAddOnSkeleton from 'components/pages/product/product_add_on_skeleton';

export const useProductOptionStyles = makeStyles((theme) => ({
  configContainer: {
    marginBottom: theme.spacing(4),
    '& > * + *': {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
  },
  fadeIn: {
    opacity: 0,
    animation: '$fadeIn 0.3s forwards',
  },
  '@keyframes fadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
}));

const ProductOptions = (props) => {
  const {
    currentProduct,
    setCurrentProduct,
    setFinancingModalOpen,
    alternatives,
    currentAltMetadata,
    addOns,
    isAddOnsLoading = false,
    isAddOnsLoadingError = false,
    appendRelationImages = () => {},
    setToast,
    cart,
  } = props;

  const classes = useProductOptionStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const { saasOnShopEnabled, show1PPDisclaimerOnPDP } = useFeatureFlags();
  const [promoEligibility, setPromoEligibility] = useState({});
  const [itemCount, setItemCount] = useState(cart.item_count);
  const [subtotal, setSubtotal] = useState(cart.subtotal);
  const [requesting, setRequesting] = useState(false);
  const [accessoryModal, setAccessoryModal] = useState({ open: false });
  const [mainProductAdded, setMainProductAdded] = useState(false);
  const [selection, setSelection] = useState({
    quantity: 1,
    addOns: {},
  });
  const [cardQuantities, setCardQuantities] = useState({});
  const [upsells, setUpsells] = useState([]);
  const [saasEligibility, setSaasEligibility] = useState({});
  const softwarePageViewed = getSoftwarePageViewed();
  const accordionRef = useRef(null);

  const cartClient = useMemo(
    () => new CartClient(cart.number, getCountryCode(), getLanguageCode()),
    [],
  );

  const eligibilityClient = useMemo(
    () => new EligibilityClient(cart.number),
    [],
  );

  const promotionEligibility = () => {
    const promosByPriority = eligibleProductPromotions(currentProduct.slug);
    getTopPromoEligibility(promosByPriority, cartClient, (eligibility) => {
      eligibility && setPromoEligibility(eligibility);
    });
  };

  useEffect(promotionEligibility, []);

  useEffect(() => {
    if (!currentProduct.featured_product) {
      return;
    }

    const pc = new ProductClient(currentProduct.slug);
    pc.fetchUpsells()
      .then((data) => {
        setUpsells(data);
        const initialCardQuantities = data
          .flatMap((catToUpsell) => catToUpsell.upsells)
          .reduce(
            (hash, upsell) => ({
              [upsell.slug]: 0,
              ...hash,
            }),
            cardQuantities,
          );
        setCardQuantities(initialCardQuantities);
      })
      .catch((e) => Sentry.capture(e));
  }, [currentProduct.slug]);

  const onAlternativeSelected = (value) => {
    if (value === null) {
      return;
    }

    // Track alternative selected events
    const selectedAlternative = alternatives.find((alt) => alt.slug === value);
    trackEvent({
      name: 'Alternative',
      value: 'Change',
      params: {
        choice: selectedAlternative.name,
        alternativeUrl: getAlternativeUrl(selectedAlternative.slug),
        cartNumber: cart.number,
        ...selectedAlternative.tracking_data,
      },
    });
    trackCdp({
      name: TRACKING_EVENT_TYPES.ChangeAlternative,
      params: {
        feature: 'shop',
        choice: selectedAlternative.name,
        alternativeUrl: getAlternativeUrl(selectedAlternative.slug),
        cartNumber: cart.number,
        ...selectedAlternative.tracking_data,
      },
    });

    // Update current window path to selected alternative slug
    window.history.replaceState(
      {},
      null,
      getAlternativeUrl(selectedAlternative.slug),
    );

    // Update PublicWebNav current URL & CTA Subnav
    updateCurrentUrl(getAlternativeUrl(selectedAlternative.slug));
    updateCtaSubnav(selectedAlternative.cta_subnav);

    // Update state
    setCurrentProduct(selectedAlternative);
    setMainProductAdded(false);
    setSelection({ quantity: 1, addOns: {} });
  };

  const onAddOnSelected = ({
    category: addOnCategory,
    newVariantId,
    choice,
  }) => {
    if (newVariantId === null) {
      return;
    }

    const newAddOns = { ...selection.addOns };
    if (newVariantId === NO_ADD_ON_VALUE) {
      delete newAddOns[addOnCategory];
      setSelection({ ...selection, addOns: newAddOns });
      appendRelationImages(null);
      return;
    }

    newAddOns[addOnCategory] = newVariantId;
    setSelection({ ...selection, addOns: newAddOns });

    const addOnByVariantId = getAddOnByVariantId(addOns, newVariantId);

    appendRelationImages(addOnByVariantId.product_key);

    // tracking
    const trackingData = addOnByVariantId.tracking_data;
    trackEvent({
      name: 'Add On',
      value: 'Change',
      params: {
        choice,
        cartNumber: cart.number,
        ...trackingData,
      },
    });
    trackCdp({
      name: TRACKING_EVENT_TYPES.ChangeAddOn,
      params: {
        feature: 'shop',
        choice,
        cartNumber: cart.number,
        ...trackingData,
      },
    });
  };

  const trackSaasClick = (name, saasEligibility = {}) => {
    trackCdp({
      name,
      params: {
        feature: 'shop',
        order_number: cart.number,
        software_page_viewed: softwarePageViewed,
        saas_eligible: saasEligibility.eligible,
        optimizely_bucket: saasEligibility.optimizely_bucket,
        experiment_key: saasEligibility.experiment_key,
      },
    });
  };

  const onAddToCartClick = () => {
    setRequesting(true);

    /**
     * toSoftwarePage - is going to the software page instead of cart
     * @param {*} options options hash
     */
    const triggerSuccessToast = (options = {}) => {
      const href =
        options?.toSoftwarePage && !softwarePageViewed
          ? `${localizedShopPath('v2')}/software`
          : `${localizedShopPath()}/cart`;

      const actionElement = (
        <MarketLink
          href={href}
          onClick={() => {
            trackViewCart({
              orderNumber: cart.number,
              trackingData: currentProduct.tracking_data,
            });

            if (options?.toSoftwarePage) {
              trackSaasClick(
                CDP_EVENT_NAMES.SAAS_ON_SHOP.VIEW_CART,
                options?.saasEligibility,
              );
            }
          }}
        >
          {t('productPage.viewCart')}
        </MarketLink>
      );

      setToast({
        timeout: 3000,
        open: true,
        message: t('productPage.addedToCart', { name: currentProduct.name }),
        variant: 'success',
        action: actionElement,
      });
    };

    const saasEligibilityCheck = async () => {
      try {
        const eligibilityResponse = await eligibilityClient.saasEligibility();

        return eligibilityResponse;
      } catch (e) {
        // Error thrown because the eligibility request failed to fire so we capture that error
        if (e instanceof Error) {
          Sentry.capture({
            message: `Failed to check saas eligibility for ${cart.number}`,
            error: e,
          });
        }

        // otherwise, the error is a promise rejection which means the merchant isn't eligible
        return {};
      }
    };

    const scrollToAccordion = () => {
      // For Square hardware products, render the accordion and scroll it into view
      if (currentProduct.featured_product) {
        setMainProductAdded(true);
        const scrollTop =
          accordionRef.current.getBoundingClientRect().top +
          window.pageYOffset -
          (STICKY_HEADER_OFFSET + 10);
        window.scrollTo({
          top: scrollTop,
          behavior: 'smooth',
        });
      }
    };

    const onSuccess = async (data) => {
      setRequesting(false);

      trackAddToCart({
        mainProductTrackingData: currentProduct.tracking_data,
        addOnsTrackingData: Object.values(selection.addOns).map(
          (variantId) => getAddOnByVariantId(addOns, variantId).tracking_data,
        ),
        cartNumber: cart.number,
        quantity: selection.quantity,
      });

      updateCartCount(data.item_count);
      setItemCount(data.item_count);
      setSubtotal(data.subtotal);

      // These are some hard coded requirements to even do the eligibility check
      // 1. The product is a 1pp product
      // 2. The product is not a R4X product
      // 3. The saas page has not been viewed
      const runSaasCheck =
        saasOnShopEnabled &&
        currentProduct.is_1pp &&
        !R4X_BASE_PRODUCT_KEYS.includes(currentProduct.base_product_key) &&
        !softwarePageViewed;

      if (runSaasCheck) {
        const saasEligibilityResponse = await saasEligibilityCheck();
        setSaasEligibility(saasEligibilityResponse);

        if (saasEligibilityResponse.eligible) {
          // trigger success toast with a link to the software page in case autoredirect fails
          triggerSuccessToast({
            toSoftwarePage: true,
            saasEligibility: saasEligibilityResponse,
          });
        } else {
          triggerSuccessToast();
        }
      } else {
        triggerSuccessToast();
      }

      scrollToAccordion();
    };

    const onError = () => {
      setRequesting(false);
      setToast({
        timeout: 3000,
        open: true,
        message: t('productPage.addToCartError', { name: currentProduct.name }),
        variant: 'critical',
      });
    };

    updateItem(
      currentProduct.variant_token,
      selection.quantity,
      Object.values(selection.addOns),
      onSuccess,
      onError,
    );
  };

  const renderPromotionBanner = () => (
    <>
      <ProductPromotionBanner
        {...promoEligibility}
        slug={currentProduct.slug}
      />
      {currentProduct.promotion_banner && (
        <InfoBubble icon={<TagIcon />}>
          <RichTextContent
            content={currentProduct.promotion_banner}
            className="medium-20"
          />
        </InfoBubble>
      )}
    </>
  );

  const renderUpsell = () => {
    trackCdp({
      name: `ProductOptions:${TRACKING_EVENT_TYPES.UpsellsRendered}`,
      params: {
        feature: 'shop',
        cartNumber: cart.number,
        product: currentProduct.tracking_data,
      },
    });

    const rows = upsells.map(({ category_name, upsells: currUpsells }) => {
      let rowTitle = t(`relations.accessoryCategories.${category_name}`);

      // Seeing only an "Other" category looks weird. Call it "Accessories"
      // when it's by itself.
      if (upsells.length === 1 && category_name === 'other') {
        rowTitle = t('relations.accessoryCategories.accessories');
      }

      return (
        <UpsellAccordion
          title={rowTitle}
          upsells={currUpsells}
          image={currUpsells[0].images.tile}
          cart={cart}
          setToast={setToast}
          setSubtotal={setSubtotal}
          setItemCount={setItemCount}
          setAccessoryModal={setAccessoryModal}
          cardQuantities={cardQuantities}
          setCardQuantities={setCardQuantities}
          key={category_name}
        />
      );
    });

    return (
      <Box
        className={classes.configContainer}
        ref={accordionRef}
        data-testid="upsell-accordion"
      >
        <h4 className="heading-30">
          {t('productPage.upsellAccordionTitle', {
            productName: t(`marketingNames.${currentProduct.marketing_name}`),
          })}
        </h4>
        <Box mb={5}>{rows}</Box>
        <SubtotalBox
          product={currentProduct}
          cart={cart}
          subtotal={subtotal}
          itemCount={itemCount}
          saasEligibility={saasEligibility}
        />
      </Box>
    );
  };

  const renderAlternatives = () => {
    if (allProductsAreColorGrouped([currentProduct, ...alternatives])) {
      return (
        <ColorProductGroup
          selectedProduct={createColorProductFromProduct(currentProduct)}
          products={filterInvalidColorProductConfigs(
            alternatives.map((alternative) =>
              createColorProductFromProduct(alternative),
            ),
          )}
          onProductSelected={(product) => {
            onAlternativeSelected(product.slug);
          }}
        />
      );
    }

    return (
      <ProductAlternatives
        currentProduct={currentProduct}
        alternatives={alternatives}
        currentAltMetadata={currentAltMetadata}
        onAlternativeSelected={onAlternativeSelected}
      />
    );
  };

  const renderAddOns = () => {
    if (
      isAddOnsLoading ||
      // The request finished loading, but the data hasn't populated yet
      (!isAddOnsLoading &&
        !isAddOnsLoadingError &&
        Object.keys(addOns).length === 0 &&
        currentProduct?.add_ons_category_count > 0)
    ) {
      return (
        <div
          style={{
            marginTop: theme.spacing(3),
            marginBottom: theme.spacing(3),
          }}
        >
          {Array.from(
            { length: currentProduct.add_ons_category_count },
            (_, index) => (
              <div key={index} style={{ marginBottom: theme.spacing(2) }}>
                <ProductAddOnSkeleton />
              </div>
            ),
          )}
        </div>
      );
    }

    return (
      <div className={classes.fadeIn}>
        {Object.keys(addOns).length > 0 &&
          Object.entries(addOns).map(([category, addOns]) => (
            <ProductAddOn
              key={category}
              category={category}
              addOns={addOns}
              selected={selection.addOns[category]}
              onSelect={onAddOnSelected}
              currentProduct={currentProduct}
            />
          ))}
      </div>
    );
  };

  return (
    <>
      <AccessoryModal
        {...accessoryModal}
        cardQuantities={cardQuantities}
        setToast={setToast}
        setSubtotal={setSubtotal}
        setCardQuantities={setCardQuantities}
      />
      <Box className={classes.configContainer}>
        <ProductHeading
          currentProduct={currentProduct}
          onFinancingTriggerClick={() => setFinancingModalOpen(true)}
        />
        {renderPromotionBanner()}
        {renderAlternatives()}
        {renderAddOns()}
        <ProductActions
          currentProduct={currentProduct}
          addOns={addOns}
          selection={selection}
          setSelection={setSelection}
          requesting={requesting}
          onAddToCartClick={onAddToCartClick}
          eligiblePromotion={promoEligibility?.eligible}
        />
        {!(
          currentProduct.shipping.out_of_stock ||
          currentProduct.shipping.no_ship
        ) && (
          <div
            data-testid="shipping-bubble-container"
            style={{ marginTop: theme.spacing(3.5) }}
          >
            <FulfillmentSection
              quote={currentProduct.shipping.ship_quote}
              expeditedAvailable={currentProduct.shipping.expedited_available}
              bopisEligible={
                currentProduct.shipping.bopis_eligible &&
                Object.values(selection.addOns).every((variantId) => {
                  const addOn = getAddOnByVariantId(addOns, variantId);
                  return addOn?.shipping?.bopis_eligible !== false;
                })
              }
              expeditedCost={currentProduct.shipping.expedited_cost}
            />
          </div>
        )}
      </Box>
      {currentProduct.featured_product ? (
        <>
          {/* SHOP-7918: Only display Contentful Disclaimer when FF is on.
            This FF exist because all product's disclaimer fields needs to be audited
            before enabled for all products.
            */}
          {mainProductAdded && renderUpsell()}
          {show1PPDisclaimerOnPDP && currentProduct?.disclaimer && (
            <RichTextContent
              content={currentProduct.disclaimer}
              color={theme.color.gray['55']}
              className="paragraph-20"
            />
          )}
        </>
      ) : (
        <ThreePPContent
          data-testid="3pp-content"
          className={classes.configContainer}
          currentProduct={currentProduct}
        />
      )}
    </>
  );
};

export default ProductOptions;
